// src/components/subscriptions/LicensePurchase.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { licenseService } from '../../services/licenseService';
import { paymentsService } from '../../services/paymentsService';
import { organizationService } from '../../services/organizationService';
import StaffSelectionModal from './StaffSelectionModal';
import DiscountRequestModal from './DiscountRequestModal';
import PaymentMethodModal from './PaymentMethodModal';
import ManualPaymentModal from './ManualPaymentModal';
import { usePageTitle } from '../../hooks/usePageTitle';
import './LicensePurchase.css';

const LicensePurchase = ({ organizationId = null, onSuccess }) => {
    usePageTitle('Purchase License');
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Check for staff_count query parameter (explicit plan pick / retry-payment flow)
    const queryParams = new URLSearchParams(location.search);
    const hasStaffCountParam = queryParams.has('staff_count');
    const initialStaffCount = parseInt(queryParams.get('staff_count')) || 5;

    const [loading, setLoading] = useState(false);
    const [pricingList, setPricingList] = useState([]);
    const [selectedStaffCount, setSelectedStaffCount] = useState(initialStaffCount);
    const [selectedDuration, setSelectedDuration] = useState(1); // Duration in months: 1, 3, 6, 12
    const [costCalculation, setCostCalculation] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [allStaff, setAllStaff] = useState([]);
    const [paymentGateways, setPaymentGateways] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [isRenewal, setIsRenewal] = useState(false);

    // Modal states
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
    const [manualPaymentData, setManualPaymentData] = useState(null);
    const [selectedGatewayDetails, setSelectedGatewayDetails] = useState(null);


    const orgId = user?.org_id;
    console.log("org id", orgId);
    // Only an ACTIVE current subscription has a real billing period to prorate
    // against (a free tier has no end_date, and an expired one has none left) -
    // in both other cases the backend bills a full new term for the chosen
    // duration instead, so the duration picker must stay visible for those.
    const isUpgradeInProgress = isRenewal && currentSubscription
        && currentSubscription.status === 'active'
        && currentSubscription.staff_count >= 5
        && selectedStaffCount > currentSubscription.staff_count;
    //  const countryCode = user?.country_code;
    const [organizationCountryCode, setOrganizationCountryCode] = useState(null);


    useEffect(() => {
        loadInitialData();
    }, [orgId]);

    useEffect(() => {
        if (selectedStaffCount > 0) {
            calculateCost();
        }
    }, [selectedStaffCount, selectedDuration]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            // Fetch organization details to get the country_code
            console.log("Fetching organization details for orgId:", orgId);
            const orgDetails = await organizationService.getOrganizationById(orgId); // Use the service
            const countryCodeFromOrg = orgDetails.country_code; // Assuming the API returns { country_code: '...' }

            console.log("Fetched country code from organization:", countryCodeFromOrg);
            setOrganizationCountryCode(countryCodeFromOrg); // Update state if needed elsewhere

            // Load pricing based on the fetched country code
            const pricing = await licenseService.getLicensePricing(countryCodeFromOrg);
            setPricingList(pricing);

            // Load payment gateways based on the fetched country code
            console.log("Fetching payment gateways for country code:", countryCodeFromOrg);
            const gateways = await paymentsService.getPaymentGatewaysByCountry(countryCodeFromOrg);
            console.log("Fetched gateways for country:", countryCodeFromOrg, gateways);
            setPaymentGateways(gateways);

            // Check if organization has an existing (active or expired) subscription to renew
            try {
                const subscription = await paymentsService.getSubscription(orgId);
                if (subscription && (subscription.status === 'active' || subscription.status === 'expired')) {
                    setCurrentSubscription(subscription);
                    setIsRenewal(true);

                    // Pre-fill the form with the current subscription's values so the
                    // org doesn't have to re-enter what they already have.
                    if (!hasStaffCountParam) {
                        setSelectedStaffCount(Number(subscription.staff_count));
                    }
                    setSelectedDuration(Number(subscription.duration_months) || 1);

                    // Load selected staff only for renewals
                    const staff = await licenseService.getSelectedStaff(orgId);
                    setSelectedStaff(staff);
                }
            } catch (err) {
                // No existing subscription - this is a new purchase
                setIsRenewal(false);
            }

        } catch (error) {
            console.error('Failed to load data:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateCost = async () => {
        try {
            setLoading(true);
            const calculation = await licenseService.calculateLicenseCost(selectedStaffCount, orgId, selectedDuration);
            setCostCalculation(calculation);
        } catch (error) {
            console.error('Failed to calculate cost:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStaffCountChange = (count) => {
        const numCount = parseInt(count);
        if (numCount < 1) return;

        setSelectedStaffCount(numCount);

        // Clear selection if count changes
        if (selectedStaff.length !== numCount) {
            setSelectedStaff([]);
        }
    };

    const handleSelectStaff = () => {
        setShowStaffModal(true);
    };

    const handleStaffSelected = (staff) => {
        setSelectedStaff(staff);
        setShowStaffModal(false);
    };

    const handleRequestDiscount = () => {
        setShowDiscountModal(true);
    };

    const handleDiscountRequested = () => {
        setShowDiscountModal(false);
        alert('Discount request submitted successfully. You will be notified once reviewed.');
    };

    const buildDiscountInvoiceContext = () => ({
        subscription_id: manualPaymentData?.subscription_id || null,
        payment_id: manualPaymentData?.payment_id || null,
        reference: manualPaymentData?.reference || null,
        staff_count: selectedStaffCount,
        duration_months: selectedDuration,
        amount: costCalculation?.total_amount || null,
        currency: costCalculation?.currency || null
    });

    const handleProceedToPayment = () => {
        // Check if this is free tier (< 5 users, i.e., 1-4 users)
        if (selectedStaffCount < 5) {
            // Free tier - activate directly without payment
            handleFreeTierActivation();
            return;
        }

        // For DOWNGRADES: selection is optional.
        // If provided, selected users are retained and others can be deactivated by backend.
        // If omitted, license count is still reduced and org can disable users manually.
        if (isRenewal && currentSubscription && selectedStaffCount < currentSubscription.staff_count) {
            if (selectedStaff.length > 0 && selectedStaff.length !== selectedStaffCount) {
                alert(
                    `You are downgrading from ${currentSubscription.staff_count} to ${selectedStaffCount} licenses.\n\n` +
                    `If you select staff, you must select exactly ${selectedStaffCount} users to keep active.`
                );
                return;
            }
            handleApplyDowngrade();
            return;
        }

        // For UPGRADES: Staff selection is NOT required
        // Existing staff remain active, new staff can be uploaded after payment

        // For NEW subscriptions: Staff selection is optional
        // Organizations can upload staff AFTER purchasing licenses
        if (!isRenewal && selectedStaff.length === 0) {
            const proceed = window.confirm(
                'No staff members selected. This is normal for a new subscription.\n\n' +
                'You can upload staff members after completing your purchase (up to your license limit).\n\n' +
                'Do you want to proceed?'
            );
            if (!proceed) return;
        }

        setShowPaymentModal(true);
    };

    const handleApplyDowngrade = async () => {
        try {
            setLoading(true);
            const paymentData = {
                organization_id: orgId,
                staff_count: selectedStaffCount,
                gateway_code: 'none',
                selected_staff_ids: selectedStaff.map(s => s.id),
                duration_months: selectedDuration
            };
            const result = await licenseService.initiateLicensePayment(paymentData);
            if (result?.no_payment_required) {
                alert(result.message || 'Downgrade applied successfully.');
                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate('/subscriptions');
                }
                return;
            }
            alert('Downgrade request submitted.');
            navigate('/subscriptions');
        } catch (error) {
            console.error('Failed to apply downgrade:', error);
            alert(error.message || 'Failed to apply downgrade');
        } finally {
            setLoading(false);
        }
    };

    const handleFreeTierActivation = async () => {
        try {
            setLoading(true);

            // Validate organization ID
            if (!orgId) {
                throw new Error('Organization ID is required. Please make sure you are logged in.');
            }

            const paymentData = {
                organization_id: orgId,
                staff_count: selectedStaffCount,
                gateway_code: 'free_tier', // Special code for free tier
                selected_staff_ids: [],
                duration_months: selectedDuration
            };

            console.log('Free tier activation data:', paymentData);

            const result = await licenseService.initiateLicensePayment(paymentData);

            if (result.is_free_tier) {
                alert(`Success! Free tier activated. You can now upload up to ${selectedStaffCount} staff members.`);
                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate('/subscriptions');
                }
            }
        } catch (error) {
            console.error('Free tier activation error:', error);
            alert('Failed to activate free tier: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentMethodSelected = async (gatewayCode) => {
        try {
            setLoading(true);
            setShowPaymentModal(false);

            const paymentData = {
                organization_id: orgId,
                staff_count: selectedStaffCount,
                gateway_code: gatewayCode,
                selected_staff_ids: selectedStaff.map(s => s.id),
                duration_months: selectedDuration
            };

            const result = await licenseService.initiateLicensePayment(paymentData);

            // Handle free tier response
            if (result.is_free_tier) {
                alert(result.message || 'Free tier activated successfully!');
                if (onSuccess) onSuccess();
                return;
            }

            if (result.no_payment_required) {
                alert(result.message || 'Request applied successfully.');
                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate('/subscriptions');
                }
                return;
            }

            if (result.payment_method === 'manual') {
                // Check if this is an existing transaction
                if (result.existing_transaction) {
                    alert(result.message || 'You have an existing pending payment for this subscription. Please check your email for bank transfer details.');
                }

                // Show bank transfer details
                // Pass the gateway details here - you might need to get them from the paymentGateways state
                const selectedGateway = paymentGateways.find(gateway => gateway.code === gatewayCode);
                if (selectedGateway) {
                    setSelectedGatewayDetails(selectedGateway); // Store the selected gateway details
                    console.log("Selected Gateway Details for Manual Payment:", selectedGateway);
                } else {
                    console.warn("Could not find gateway details for code:", gatewayCode);
                    // Optionally set a default or show an error if gateway details are missing
                    setSelectedGatewayDetails(null);
                }
                setManualPaymentData(result);
                setShowManualPaymentModal(true);
            } else {
                // Redirect to payment gateway
                window.location.href = result.authorization_url;
            }

        } catch (error) {
            console.error('Payment initiation failed:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleManualPaymentConfirmed = async () => {
        try {
            await licenseService.confirmManualPayment(manualPaymentData.payment_id);
            setShowManualPaymentModal(false);

            if (onSuccess) {
                onSuccess();
            } else {
                alert('Payment confirmation received. Your license will be activated within 24 hours.');
                navigate('/subscriptions');
            }
        } catch (error) {
            console.error('Failed to confirm payment:', error);
            alert(error.message);
        }
    };

    const getRecommendedPlan = () => {
        // Find the closest pricing tier
        return pricingList.find(p => p.user_count >= selectedStaffCount) ||
            pricingList[pricingList.length - 1];
    };

    // Show error if no organization ID
    if (!orgId && !organizationId) {
        return (
            <div className="license-purchase-loading">
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <p>Unable to load subscription information. Please make sure you are logged in with a valid organization account.</p>
                </div>
            </div>
        );
    }

    if (loading && !costCalculation) {
        return (
            <div className="license-purchase-loading">
                <div className="spinner"></div>
                <p>Loading license options...</p>
            </div>
        );
    }

    const recommended = getRecommendedPlan();

    return (
        <div className="license-purchase-container">
            <div className="license-purchase-header">
                <h2>Purchase License</h2>
                <p>Select the number of staff members you want to include in your subscription</p>
            </div>

            {/* Pricing Plans Display */}
            <div className="pricing-plans">
                <h3>Available Plans</h3>
                <div className="pricing-grid">
                    {pricingList.filter(p => p.user_count <= 10).map((plan) => (
                        <div
                            key={plan.id}
                            className={`pricing-card ${selectedStaffCount === plan.user_count ? 'selected' : ''} ${plan.user_count === recommended?.user_count ? 'recommended' : ''}`}
                            onClick={() => handleStaffCountChange(plan.user_count)}
                        >
                            {plan.user_count === recommended?.user_count && (
                                <div className="recommended-badge">Recommended</div>
                            )}
                            <div className="plan-staff-count">{plan.user_count} Staff</div>
                            <div className="plan-price">
                                <span className="currency">{plan.currency}</span>
                                <span className="amount">{parseFloat(plan.total_price).toFixed(2)}</span>
                                <span className="period">/month</span>
                            </div>
                            {plan.user_count > 4 && (
                                <div className="plan-setup-fee">
                                    Setup Fee: {plan.currency} {parseFloat(plan.setup_fee).toFixed(2)}
                                </div>
                            )}
                            {plan.user_count <= 4 && (
                                <div className="plan-free-badge">FREE</div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="custom-plan">
                    <label>
                        <span>Or enter custom number of staff:</span>
                        <input
                            type="number"
                            min="1"
                            value={selectedStaffCount}
                            onChange={(e) => handleStaffCountChange(e.target.value)}
                            className="staff-count-input"
                        />
                    </label>
                </div>
            </div>

            {/* Duration Selection - not applicable when upgrading an active subscription:
                added licenses are prorated to the current period, which does not extend. */}
            {selectedStaffCount >= 5 && !isUpgradeInProgress && (
                <div className="duration-section">
                    <h3>
                        <i className="fas fa-calendar-alt"></i> Subscription Duration
                    </h3>
                    <p>Choose how many months to pay in advance</p>
                    <div className="duration-options">
                        {[
                            { months: 1, label: '1 Month', discount: null },
                            { months: 3, label: '3 Months', discount: null },
                            { months: 6, label: '6 Months', discount: null },
                            { months: 12, label: '12 Months', discount: null }
                        ].map(option => (
                            <button
                                key={option.months}
                                className={`duration-btn ${selectedDuration === option.months ? 'selected' : ''}`}
                                onClick={() => setSelectedDuration(option.months)}
                            >
                                <div className="duration-label">{option.label}</div>
                                {option.discount && (
                                    <div className="duration-discount">{option.discount}</div>
                                )}
                                <div className="duration-check">
                                    {selectedDuration === option.months && <i className="fas fa-check-circle"></i>}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {isUpgradeInProgress && (
                <div className="duration-section">
                    <h3>
                        <i className="fas fa-info-circle"></i> Billing Period
                    </h3>
                    <p>
                        Your subscription end date stays the same
                        {currentSubscription?.end_date && (
                            <> ({new Date(currentSubscription.end_date).toLocaleDateString()})</>
                        )}. You're only charged for the added licenses for the time remaining in your
                        current period{costCalculation?.prorated_days_remaining != null
                            ? ` (${costCalculation.prorated_days_remaining} day(s))`
                            : ''} - not a new full term.
                    </p>
                </div>
            )}

            {/* Cost Calculation Summary */}
            {costCalculation && (
                <div className="cost-summary">
                    <h3>Cost Summary</h3>
                    <div className="summary-details">
                        <div className="summary-row">
                            <span>Number of Staff:</span>
                            <strong>{costCalculation.staff_count}</strong>
                        </div>
                        {costCalculation.duration_months > 1 && !isUpgradeInProgress && (
                            <div className="summary-row">
                                <span>Duration:</span>
                                <strong>{costCalculation.duration_months} Month{costCalculation.duration_months > 1 ? 's' : ''}</strong>
                            </div>
                        )}
                        {isUpgradeInProgress && costCalculation.prorated_days_remaining != null && (
                            <div className="summary-row">
                                <span>Billed for:</span>
                                <strong>{costCalculation.prorated_days_remaining} day(s) remaining</strong>
                            </div>
                        )}
                        <div className="summary-row">
                            <span>{costCalculation.duration_months > 1 ? 'Monthly Price:' : 'Unit Price:'}</span>
                            <span>{costCalculation.currency} {parseFloat(costCalculation.monthly_price || costCalculation.unit_price).toFixed(2)}/month</span>
                        </div>
                        <div className="summary-row">
                            <span>Subtotal {isUpgradeInProgress
                                ? (costCalculation.prorated_days_remaining != null ? `(${costCalculation.prorated_days_remaining} days)` : '')
                                : (costCalculation.duration_months > 1 ? `(${costCalculation.duration_months} months)` : '')}:</span>
                            <span>{costCalculation.currency} {parseFloat(costCalculation.subtotal).toFixed(2)}</span>
                        </div>

                        {costCalculation.setup_fee > 0 && (
                            <div className="summary-row setup-fee">
                                <span>
                                    {costCalculation.is_upgrade && costCalculation.additional_licenses > 0
                                        ? `Setup Fee (for ${costCalculation.additional_licenses} additional license${costCalculation.additional_licenses === 1 ? '' : 's'}):`
                                        : 'One-time Setup Fee:'}
                                </span>
                                <span>{costCalculation.currency} {parseFloat(costCalculation.setup_fee).toFixed(2)}</span>
                            </div>
                        )}

                        {costCalculation.discount_amount > 0 && (
                            <div className="summary-row discount">
                                <span>Discount ({costCalculation.discount_percentage}%):</span>
                                <span>-{costCalculation.currency} {parseFloat(costCalculation.discount_amount).toFixed(2)}</span>
                            </div>
                        )}

                        {costCalculation.tax_amount > 0 && (
                            <div className="summary-row tax">
                                <span>Tax ({costCalculation.tax_percentage}%):</span>
                                <span>{costCalculation.currency} {parseFloat(costCalculation.tax_amount).toFixed(2)}</span>
                            </div>
                        )}

                        <div className="summary-row total">
                            <span>Total Amount:</span>
                            <strong>{costCalculation.currency} {parseFloat(costCalculation.total_amount).toFixed(2)}</strong>
                        </div>

                        {costCalculation.is_upgrade && (
                            <div className="upgrade-notice">
                                <i className="fas fa-arrow-up"></i>
                                <span>Upgrading from {costCalculation.current_license_count} to {costCalculation.staff_count} staff</span>
                            </div>
                        )}

                        {costCalculation.is_downgrade && (
                            <div className="downgrade-notice">
                                <i className="fas fa-arrow-down"></i>
                                <span>Downgrading from {costCalculation.current_license_count} to {costCalculation.staff_count} staff</span>
                            </div>
                        )}
                    </div>

                    {/* Discount Request */}
                    {costCalculation.discount_percentage == 0.00 && (
                        <button
                            className="discount-request-btn"
                            onClick={handleRequestDiscount}
                            disabled={selectedStaffCount <= 4 || !manualPaymentData?.payment_id}
                            title={selectedStaffCount <= 4 ? 'Discount not available for free tier (<=4 users)' : !manualPaymentData?.payment_id ? 'Generate a proforma invoice first (Bank Transfer), then request discount' : 'Request a discount for this invoice'}
                        >
                            <i className="fas fa-tag"></i>
                            Request Discount
                        </button>
                    )}
                </div>
            )}

            {/* Staff Selection - Only show for DOWNGRADES (reducing license count) */}
            {isRenewal && currentSubscription && selectedStaffCount < currentSubscription.staff_count ? (
                <div className="staff-selection-section">
                    <h3>
                        <i className="fas fa-arrow-down"></i> Downgrade: Select Staff to Keep
                    </h3>
                    <p>
                        <span className="required-notice">
                            <i className="fas fa-exclamation-circle"></i>
                            You are downgrading from {currentSubscription.staff_count} to {selectedStaffCount} licenses.
                            You may select exactly {selectedStaffCount} staff members to keep active now,
                            or proceed without selection and disable users manually later.
                        </span>
                    </p>

                    <div className="selected-staff-preview">
                        {selectedStaff.length > 0 ? (
                            <>
                                <div className="selected-count">
                                    {selectedStaff.length} of {selectedStaffCount} selected
                                </div>
                                <div className="selected-staff-list">
                                    {selectedStaff.map(staff => (
                                        <div key={staff.id} className="staff-chip">
                                            <i className="fas fa-user"></i>
                                            <span>{staff.name || staff.full_name}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="no-selection">
                                <i className="fas fa-users"></i>
                                <p>No staff selected yet - you must select {selectedStaffCount} staff to keep</p>
                            </div>
                        )}
                    </div>

                    <button
                        className="select-staff-btn"
                        onClick={handleSelectStaff}
                    >
                        <i className="fas fa-users-cog"></i>
                        {selectedStaff.length > 0 ? 'Change Selection' : 'Select Staff to Keep'}
                    </button>
                </div>
            ) : isUpgradeInProgress ? (
                <div className="info-notice upgrade-notice-box">
                    <i className="fas fa-arrow-up"></i>
                    <div>
                        <h4>Upgrade: Adding More Licenses</h4>
                        <p>
                            You are upgrading from {currentSubscription.staff_count} to {selectedStaffCount} licenses.
                            Your existing {currentSubscription.staff_count} staff will remain active.
                            After completing payment, you can upload {selectedStaffCount - currentSubscription.staff_count} additional staff members.
                        </p>
                        <p className="setup-fee-notice">
                            <i className="fas fa-info-circle"></i>
                            <strong>Setup Fee:</strong> You will only pay the setup fee for the {selectedStaffCount - currentSubscription.staff_count} additional licenses.
                        </p>
                    </div>
                </div>
            ) : isRenewal && currentSubscription && currentSubscription.staff_count < 5 && selectedStaffCount > currentSubscription.staff_count ? (
                <div className="info-notice">
                    <i className="fas fa-arrow-up"></i>
                    <div>
                        <h4>Moving to a Paid Subscription</h4>
                        <p>
                            You're moving from the free tier ({currentSubscription.staff_count} staff) to a paid plan
                            of {selectedStaffCount} staff. Since the free tier was never billed, this is charged as a
                            full new purchase - including the setup fee and your chosen billing duration below.
                        </p>
                    </div>
                </div>
            ) : isRenewal && currentSubscription && currentSubscription.status !== 'active'
                && currentSubscription.staff_count >= 5 && selectedStaffCount !== currentSubscription.staff_count ? (
                <div className="info-notice">
                    <i className="fas fa-redo"></i>
                    <div>
                        <h4>Renewing at a Different License Count</h4>
                        <p>
                            Your previous subscription ({currentSubscription.staff_count} staff) has lapsed, so this
                            renewal is billed as a full new term for {selectedStaffCount} staff at your chosen billing
                            duration below.
                            {selectedStaffCount > currentSubscription.staff_count
                                ? ' The setup fee only covers the licenses beyond what you already paid for previously.'
                                : ' No setup fee applies since it was already paid on your original purchase.'}
                        </p>
                    </div>
                </div>
            ) : !isRenewal && selectedStaffCount < 5 ? (
                <div className="info-notice">
                    <i className="fas fa-gift"></i>
                    <div>
                        <h4>Free Tier Subscription</h4>
                        <p>
                            You can upload up to {selectedStaffCount} staff members after activating your free subscription.
                            This subscription never expires!
                        </p>
                    </div>
                </div>
            ) : !isRenewal ? (
                <div className="info-notice">
                    <i className="fas fa-info-circle"></i>
                    <div>
                        <h4>New Subscription</h4>
                        <p>
                            For new subscriptions, you don't need to select staff members now.
                            After completing your purchase, you can upload up to {selectedStaffCount} staff members.
                        </p>
                    </div>
                </div>
            ) : null}

            {/* Action Buttons */}
            <div className="purchase-actions">
                <button
                    className={`proceed-btn ${isUpgradeInProgress ? 'proceed-btn-upgrade' : ''}`}
                    onClick={handleProceedToPayment}
                    disabled={
                        loading ||
                        // For DOWNGRADES only: staff selection is required
                        (isRenewal && currentSubscription && selectedStaffCount < currentSubscription.staff_count && selectedStaff.length > 0 && selectedStaff.length !== selectedStaffCount)
                    }
                >
                    {loading ? (
                        <>
                            <span className="spinner-small"></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-credit-card"></i>
                            {selectedStaffCount < 5 ? 'Activate Free Subscription' :
                                isUpgradeInProgress ? 'Upgrade Subscription' :
                                    isRenewal && currentSubscription && selectedStaffCount < currentSubscription.staff_count ? 'Downgrade Subscription' :
                                        'Proceed to Payment'}
                        </>
                    )}
                </button>
            </div>

            {/* Modals */}
            {showStaffModal && (
                <StaffSelectionModal
                    requiredCount={selectedStaffCount}
                    selectedStaff={selectedStaff}
                    organizationId={orgId}
                    onSelect={handleStaffSelected}
                    onClose={() => setShowStaffModal(false)}
                />
            )}

            {showDiscountModal && (
                <DiscountRequestModal
                    onSubmit={handleDiscountRequested}
                    onClose={() => setShowDiscountModal(false)}
                    invoiceContext={buildDiscountInvoiceContext()}
                />
            )}

            {showPaymentModal && (
                <PaymentMethodModal
                    gateways={paymentGateways}
                    amount={costCalculation?.total_amount}
                    currency={costCalculation?.currency}
                    breakdown={costCalculation}
                    onSelect={handlePaymentMethodSelected}
                    onClose={() => setShowPaymentModal(false)}
                />
            )}

            {showManualPaymentModal && manualPaymentData && selectedGatewayDetails && (
                <ManualPaymentModal
                    paymentData={manualPaymentData}
                    gatewayDetails={selectedGatewayDetails}
                    organizationCountryCode={organizationCountryCode}
                    onConfirm={handleManualPaymentConfirmed}
                    onClose={() => setShowManualPaymentModal(false)}
                    onRequestDiscount={handleRequestDiscount}
                    hasDiscount={Number(costCalculation?.discount_percentage || 0) > 0}
                />
            )}
        </div>
    );
};

export default LicensePurchase;

